import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';

/**
 * Hook that updates the user's last seen timestamp periodically
 * and provides the update function for other components
 */
export const useLastSeen = () => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { user, initialized } = useAuthContext();

  const updateLastSeen = useCallback(async () => {
    try {
      if (!user || !initialized) return;

      const now = new Date().toISOString();

      // Use upsert to insert or update
      const { error } = await supabase
        .from('user_last_seen')
        .upsert(
          { user_id: user.id, last_seen_at: now },
          { onConflict: 'user_id', ignoreDuplicates: false }
        );

      if (error && !error.message?.includes('AbortError') && !error.message?.includes('Load failed')) {
        console.warn('Last seen update skipped');
      }
    } catch {
      // Silently ignore - network aborts expected during navigation
    }
  }, [initialized, user]);

  useEffect(() => {
    // Update immediately when hook mounts
    updateLastSeen();

    // Update every 60 seconds while user is active
    intervalRef.current = setInterval(() => {
      updateLastSeen();
    }, 60000);

    // Also update on visibility change (when tab becomes visible)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateLastSeen();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [updateLastSeen]);

  return { updateLastSeen };
};
